import { rankByWorth } from '@tigrinho/engine';
import { navigate } from '../App';
import { asState } from '../game/selectors';
import { ART, pawnArt } from '../lib/art';
import { SEAT_COLORS } from '../lib/content';
import { useRoomCtx } from './RoomScreen';
import { PawnFace } from '../game/PawnFace';

const WIN_TEXT: Record<string, string> = {
  time: 'Acabou o tempo: venceu o maior patrimônio.',
  lastStanding: 'Todo mundo faliu, só sobrou um.',
  triple: 'Fechou 3 grupos de cor: vitória na hora!',
  line: 'Fechou um lado inteiro do tabuleiro: vitória na hora!',
  resorts: 'Juntou os 4 resorts: vitória na hora!',
};

interface Award {
  title: string;
  who: string;
  detail: string;
}

/** Tela de fim: pódio, prêmios da zoeira e revanche. */
export function End({ onClose }: { onClose: () => void }) {
  const { room, you, send, name, money, seatIndex } = useRoomCtx();
  const g = room.game!;
  const ranking = rankByWorth(asState(g));
  const isHost = room.hostId === you;
  const winners = new Set(g.winners);

  const best = <T,>(pick: (id: string) => number, min = 1): [string, number] | null => {
    let top: [string, number] | null = null;
    for (const p of g.players) {
      const v = pick(p.id);
      if (v >= min && (!top || v > top[1])) top = [p.id, v];
    }
    return top;
  };
  const stat = (id: string) => g.players.find((p) => p.id === id)!.stats;
  const awards: Award[] = [];
  const add = (title: string, r: [string, number] | null, detail: (v: number) => string) => {
    if (r) awards.push({ title, who: name(r[0]), detail: detail(r[1]) });
  };
  add('Maior facada levada', best((id) => stat(id).biggestRentPaid, 100), (v) => `pagou ${money(v)} de uma vez`);
  add('Mais atacado', best((id) => stat(id).attacksReceived), (v) => `${v} ${v === 1 ? 'ataque' : 'ataques'} sofridos`);
  add('Mais vingativo', best((id) => stat(id).attacksMade), (v) => `${v} ${v === 1 ? 'ataque' : 'ataques'} feitos`);
  add('Senhorio do ano', best((id) => stat(id).rentReceived, 50), (v) => `recebeu ${money(v)} de aluguel`);
  add('Maior tacador de tomate', best((id) => room.taunts[id]?.thrown ?? 0), (v) => `${v} provocações`);
  add('Alvo favorito da galera', best((id) => room.taunts[id]?.received ?? 0), (v) => `levou ${v} provocações`);
  add('Rei da tomada', best((id) => stat(id).takeoversMade), (v) => `tomou ${v} ${v === 1 ? 'cidade' : 'cidades'}`);
  add('Mais roubado', best((id) => stat(id).takeoversSuffered), (v) => `perdeu ${v} ${v === 1 ? 'cidade' : 'cidades'} na tomada`);

  return (
    <div className="end-backdrop">
      <section className="end" aria-labelledby="end-title">
        <i className="sheet-paper" aria-hidden="true" />
        <div className="ribbon ribbon-yellow end-ribbon">
          <h2>Fim de jogo</h2>
        </div>
        <div className="end-hero" aria-hidden="true">
          <img className="end-trophy" src={ART.trophy} alt="" />
          {[...winners].slice(0, 2).map((id) => (
            <img
              key={id}
              className="end-winner"
              src={pawnArt(room.seats.find((s) => s.id === id)?.pawn ?? 0).happy}
              alt=""
            />
          ))}
          <span className="coin-rain">
            {Array.from({ length: 16 }, (_, i) => (
              <img key={i} src={ART.money} alt="" style={{ left: `${(i * 29) % 100}%`, animationDelay: `${(i % 8) * 0.18}s` }} />
            ))}
          </span>
        </div>
        <h1 id="end-title" className="end-title">
          {winners.has(you ?? '') ? 'Você ganhou!' : `${[...winners].map(name).join(' e ')} ${winners.size > 1 ? 'ganharam' : 'ganhou'}`}
        </h1>
        {g.winReason && <p className="end-reason">{WIN_TEXT[g.winReason] ?? ''}</p>}
        <ol className="podium">
          {ranking.map((r, i) => {
            const seat = room.seats.find((s) => s.id === r.player);
            return (
              <li
                key={r.player}
                className={winners.has(r.player) ? 'is-winner' : ''}
                style={{ '--seat': SEAT_COLORS[seatIndex(r.player)] } as React.CSSProperties}
              >
                <span className="podium-pos">{i + 1}º</span>
                <span className="podium-pawn" aria-hidden="true">
                  <PawnFace
                    pawn={seat?.pawn ?? 0}
                    mood={winners.has(r.player) ? 'happy' : i === ranking.length - 1 ? 'sad' : 'idle'}
                  />
                </span>
                <span className="podium-name">{r.player === you ? 'Você' : name(r.player)}</span>
                <span className="podium-worth">{g.players.find((p) => p.id === r.player)?.eliminated ? 'Faliu' : money(r.worth)}</span>
              </li>
            );
          })}
        </ol>
        {awards.length > 0 && (
          <>
            <h3 className="end-sub">Prêmios da zoeira</h3>
            <ul className="awards">
              {awards.map((a) => (
                <li key={a.title}>
                  <strong>{a.title}</strong>
                  <span>
                    {a.who}, {a.detail}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="end-actions">
          {isHost ? (
            <button className="pill pill-blue pill-big" onClick={() => send({ t: 'rematch' })}>
              Revanche
            </button>
          ) : (
            <p className="foot-hint">Esperando quem criou a mesa chamar a revanche</p>
          )}
          <button className="pill pill-gray" onClick={onClose}>
            Ver o tabuleiro
          </button>
          <button className="pill pill-gray" onClick={() => navigate('/')}>
            Sair
          </button>
        </div>
      </section>
    </div>
  );
}
